// packages
import { index } from 'd3-array';
import { derived, writable } from 'svelte/store';

// local
import { find } from './utils/array.js';
import { getSeatNumber, getTags, hasOffice } from './utils/race-helpers.js';
import { RACE_URL, TRENDS } from './constants.js';

/**
 * @template T
 * @typedef {import('svelte/store').Readable<T>} Readable
 */

/**
 * @typedef {import('./types').Race} Race
 * @typedef {import('./types').ScopeFeed} ScopeFeed
 * @typedef {import('./types').TrendsFeed} TrendsFeed
 */

/** @type {Map<string, Promise<Readable<any>>>} */
const feedCache = new Map();

// A request still going after this long is cancelled (and retried)
const REQUEST_TIMEOUT = 15 * 1000;

// First load only: how long to wait before each retry. After the last one
// the feed fails, so a missing race file doesn't hold up an embed for long.
const RETRY_DELAYS = [2 * 1000, 5 * 1000];

/**
 * Returns a polling store for the given URL, creating it only once per URL.
 * Subsequent calls with the same URL return the cached Promise so only one
 * fetch and one 30-second polling interval exist per feed, regardless of how
 * many embeds subscribe. The data inside the store still refreshes every
 * `seconds` seconds — nothing about update frequency changes.
 * To test with local copies of the feeds, set USE_LOCAL_FEEDS in constants.js.
 * @template [T=ScopeFeed]
 * @param {string} url The URL to request
 * @param {number} seconds How many seconds to wait between new requests
 * @returns {Promise<Readable<T>>}
 */
export function createFeed(url, seconds = 30) {
  const cached = feedCache.get(url);
  if (cached) return cached;
  /** @type {Promise<Readable<T>>} */
  const promise = buildFeed(url, seconds);
  feedCache.set(url, promise);
  // don't keep a failed first load: the next embed to ask tries again
  promise.catch(() => feedCache.delete(url));
  return promise;
}

/**
 * Fetches and parses one feed, giving up after REQUEST_TIMEOUT
 * @param {string} url
 */
async function fetchJson(url) {
  const controller = new AbortController();
  const timer = window.setTimeout(() => controller.abort(), REQUEST_TIMEOUT);

  try {
    const response = await fetch(url, { signal: controller.signal });
    if (!response.ok) {
      throw Object.assign(
        new Error(`${response.status} ${response.statusText}: ${url}`),
        { status: response.status }
      );
    }
    // awaited here so the timeout covers downloading the body too
    return await response.json();
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Worth trying again: timeouts, network errors and server errors (5xx).
 * Not 4xx: the file isn't there (yet), and asking again right away won't help.
 * @param {any} error
 */
const isRetryable = (error) => !(error?.status >= 400 && error?.status < 500);

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => window.setTimeout(resolve, ms));

/**
 * @template T
 * @param {string} url
 * @param {number} seconds
 * @returns {Promise<Readable<T>>}
 */
async function buildFeed(url, seconds) {
  const interval = seconds * 1000;

  /** @type {number | undefined} */
  let timeoutId;

  /** @type {number} */
  let lastUpdated;

  // true while someone is subscribed and the tab is visible
  let polling = false;

  // true while a request is out, so a second one isn't started alongside it
  let inFlight = false;

  async function makeRequest() {
    const feed = /** @type {T} */ (await fetchJson(url));
    lastUpdated = Date.now();
    return feed;
  }

  async function firstLoad() {
    for (let attempt = 0; ; attempt++) {
      try {
        return await makeRequest();
      } catch (error) {
        if (attempt >= RETRY_DELAYS.length || !isRetryable(error)) throw error;
        console.warn(`Couldn't load ${url}; retrying`, error);
        await wait(RETRY_DELAYS[attempt]);
      }
    }
  }

  const feed = await firstLoad();

  const { subscribe } = writable(feed, function start(set) {
    // clears the last timer first, so there's never more than one
    /** @param {number} delay */
    function schedule(delay) {
      clearTimeout(timeoutId);
      timeoutId = window.setTimeout(run, delay);
    }

    async function run() {
      inFlight = true;
      try {
        set(await makeRequest());
      } catch (error) {
        // keep showing the last data and try again next time
        console.error(`Couldn't refresh ${url}; retrying`, error);
      } finally {
        inFlight = false;
        // if the tab was hidden or everyone unsubscribed while this request
        // was out, wait for resume() instead of polling in the background
        if (polling) schedule(interval);
      }
    }

    function resume() {
      polling = true;
      // the request that's already out schedules the next one when it's done
      if (inFlight) return;
      const elapsed = Date.now() - lastUpdated;
      if (elapsed < interval) {
        schedule(interval - elapsed);
      } else {
        run();
      }
    }

    function pause() {
      polling = false;
      clearTimeout(timeoutId);
      timeoutId = undefined;
    }

    function handleVisibilityChange() {
      if (document.hidden) {
        pause();
      } else {
        resume();
      }
    }

    document.addEventListener('visibilitychange', handleVisibilityChange);

    // a page opened in a background tab starts polling once it's shown
    if (!document.hidden) resume();

    return function stop() {
      pause();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  });

  return { subscribe };
}

/**
 * Fetches one race's own file (the only place countyResults appear).
 * Same as createFeed(RACE_URL(id, source)), but returns a single race, not an array.
 * @param {string} id e.g. 'CA-6123'
 * @param {string} [source] 'ap' (default) or 'lavote' for L.A. races
 * @returns {Promise<Readable<Race>>}
 */
export function createSingleRaceFeed(id, source = 'ap') {
  return createFeed(RACE_URL(id, source));
}

/**
 * Fetches the balance of power feed (TRENDS): U.S. House, Senate, governors
 * and California's House delegation, under data.attributes.
 * @returns {Promise<Readable<TrendsFeed>>}
 */
export function createTrendsFeed() {
  return createFeed(TRENDS);
}

/**
 * @typedef {Object} FilterSpec
 * @property {string[]} [offices] keep races with any of these office codes ('Y', 'H'...)
 * @property {string[]} [tagsAny] keep races with at least one of these tags
 * @property {string[]} [tagsAll] keep races with every one of these tags
 * @property {string[]} [excludeTags] drop races with any of these tags
 * @property {string | string[]} [electionType] 'race' | 'proposition' | 'measure'
 * @property {string} [level] 'california' | 'federal'
 * @property {string[]} [jurisdictions] keep races in any of these jurisdictions ('Los Angeles', 'Burbank'...)
 * @property {string[]} [ids] keep exactly these race IDs
 * @property {string[]} [excludeIds] drop these race IDs (e.g. an L.A. Vote race shown from AP instead)
 * @property {number[]} [seats] keep races with these seat/district numbers (see getSeatNumber)
 * @property {string} [match] regex tested against attributes.title
 * @property {'seat' | 'title' | 'ids' | 'offices' | 'none'} [sort] defaults to 'ids' when ids is set, otherwise 'none'.
 *   'offices' keeps races in the order the offices are listed
 */

/**
 * Picks the races a page asks for out of a whole feed, in the order it wants.
 * Updates automatically whenever the feed does.
 * @param {Readable<ScopeFeed>} store
 * @param {FilterSpec} spec
 * @returns {Readable<Race[]>}
 */
export function createFilteredFeed(store, spec = {}) {
  const {
    offices,
    tagsAny,
    tagsAll,
    excludeTags,
    level,
    jurisdictions,
    ids,
    excludeIds,
    seats,
    match,
  } = spec;
  const electionTypes =
    typeof spec.electionType === 'string'
      ? [spec.electionType]
      : spec.electionType;
  const matcher = match ? new RegExp(match) : null;
  const sort = spec.sort ?? (ids ? 'ids' : 'none');

  /** @param {Race} race */
  function keep(race) {
    const tags = getTags(race);
    const { attributes } = race;

    if (offices && !offices.some((code) => hasOffice(race, code))) return false;
    if (tagsAny && !tagsAny.some((tag) => tags.includes(tag))) return false;
    if (tagsAll && !tagsAll.every((tag) => tags.includes(tag))) return false;
    if (excludeTags && excludeTags.some((tag) => tags.includes(tag)))
      return false;
    if (electionTypes && !electionTypes.includes(attributes.electionType))
      return false;
    if (level && attributes.level !== level) return false;
    if (jurisdictions && !jurisdictions.includes(attributes.jurisdiction))
      return false;
    if (ids && !ids.includes(race.id)) return false;
    if (excludeIds && excludeIds.includes(race.id)) return false;
    if (seats && !seats.includes(getSeatNumber(race))) return false;
    if (matcher && !matcher.test(attributes.title)) return false;
    return true;
  }

  return derived(store, ($feed) => {
    const races = $feed.filter(keep);

    if (sort === 'seat') {
      races.sort((a, b) => getSeatNumber(a) - getSeatNumber(b));
    } else if (sort === 'title') {
      races.sort((a, b) =>
        a.attributes.title.localeCompare(b.attributes.title)
      );
    } else if (sort === 'ids' && ids) {
      races.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
    } else if (sort === 'offices' && offices) {
      /** @param {Race} race */
      const officeIndex = (race) =>
        offices.findIndex((code) => hasOffice(race, code));
      races.sort((a, b) => officeIndex(a) - officeIndex(b));
    }

    return races;
  });
}

/**
 * Picks one race out of a feed that's already loaded
 * @param {Readable<ScopeFeed>} store
 * @param {string} raceId e.g. 'CA-6123'
 * @returns {Readable<Race | undefined>}
 */
export function createRaceFeed(store, raceId) {
  return derived(store, ($feed) => find($feed, (race) => race.id === raceId));
}

/**
 * Counties of a single race, keyed by fipsCode (matches the geography codes)
 * @param {Readable<Race>} store
 * @returns {Readable<import('./types').CountyLookup>}
 */
export function createCountyLookup(store) {
  return derived(store, ($race) =>
    index($race.attributes.countyResults ?? [], (d) => d.fipsCode)
  );
}

/**
 * Choices of a single race, keyed by choiceId (what county results reference)
 * @param {Readable<Race>} store
 * @returns {Readable<import('./types').ChoiceLookup>}
 */
export function createChoiceLookup(store) {
  return derived(store, ($race) =>
    index($race.attributes.choices, (d) => d.choiceId)
  );
}
